import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, ScrollView, Pressable, Icon3 } from '../lib/react-native-compat';
import { useAtom } from 'jotai';
import {
    selectedJobAtom,
    selectedVersionAtom,
    jobUserDataAtom,
    ohPercentAtom,
    overheadDataAtom,
    selectedTeamAtom,
} from '../store/company';
import { pNum, fNum } from '../db/company';
import { getCoDocs } from './cologDocsApi';
import { downloadCoLog } from './cologPsrXlsx';

// Normalize a CO label / SharePoint folder name to a canonical key so a row
// (e.g. "02") matches its Change Orders subfolder (e.g. "CO-02 - ...").
const coDocKey = (s) => {
    const str = String(s || '').trim().toUpperCase();
    let m = str.match(/^I[-\s]*0*(\d+)/);          // internal change: "I-01"
    if (m) return 'I' + parseInt(m[1], 10);
    m = str.match(/^(?:CO)?[-\s]*0*(\d+)/);         // regular CO, optional "CO" prefix
    if (m) return 'C' + parseInt(m[1], 10);
    return null;
};

// Same idea, but for a SharePoint document/folder NAME rather than a CO row
// label. Filed docs embed the CO token mid-name — e.g.
//   "LARO-WHCA Project- CO-001 - signed.pdf"
// so we scan for a CO-/I- token anywhere in the string instead of anchoring at
// the start (which only fit bare row labels like "02" / "CO-02 - ...").
const coDocKeyFromName = (s) => {
    const str = String(s || '').toUpperCase();
    let m = str.match(/\bI[-\s]*0*(\d+)/);          // internal change: "... I-01 ..."
    if (m) return 'I' + parseInt(m[1], 10);
    m = str.match(/\bCO[-\s]*0*(\d+)/);             // regular CO: "... CO-001 ..."
    if (m) return 'C' + parseInt(m[1], 10);
    return null;
};

const COLogPSRView = (a) => {
    const [selectedJob] = useAtom(selectedJobAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [jobUserData] = useAtom(jobUserDataAtom);
    const [ohPercent, setOhPercent] = useAtom(ohPercentAtom);
    const [overheadData] = useAtom(overheadDataAtom);
    const [selectedTeam] = useAtom(selectedTeamAtom);
    const [customerViewRows, setCustomerViewRows] = useState([]);
    const [customerViewSections, setCustomerViewSections] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [coDocsInfo, setCoDocsInfo] = useState(null);
    const [exporting, setExporting] = useState(false);
    const sectionRefs = useRef([]);

    // Fetch which COs have a supporting document filed in SharePoint.
    useEffect(() => {
        let cancelled = false;
        if (!selectedJob) { setCoDocsInfo(null); return; }
        getCoDocs(selectedJob)
            .then((info) => { if (!cancelled) setCoDocsInfo(info); })
            .catch(() => { if (!cancelled) setCoDocsInfo(null); });
        return () => { cancelled = true; };
    }, [selectedJob]);

    // Set of CO keys that have a matching subfolder/file in the CO docs folder.
    const documentedCoKeys = React.useMemo(() => {
        const set = new Set();
        if (coDocsInfo) {
            [...(coDocsInfo.subfolders || []), ...(coDocsInfo.files || [])].forEach((it) => {
                const k = coDocKeyFromName(it.name);
                if (k) set.add(k);
            });
        }
        return set;
    }, [coDocsInfo]);

    // Warn only for customer-facing COs with no matching document. Internal
    // Change rows never require a SharePoint document, so they don't warn.
    const isCoMissingDoc = (r) => {
        if (!coDocsInfo) return false;
        const key = coDocKey(r.Row_Label);
        if (!key) return false;
        if (key[0] === 'I') return false;
        if (/internal/i.test(r.Type || '')) return false;
        return !documentedCoKeys.has(key);
    };
    const coDocsTooltip = 'No change-order document filed in SharePoint (03.09 - Change Orders)';

    // Read-only signature-verification results, persisted by the entry view in
    // Forecast_User_Data under the "Change Order Signature" tab. PSR never runs
    // the verifier — it only reflects the last persisted result per CO.
    const sigResults = React.useMemo(() => {
        const map = {};
        if (!jobUserData) return map;
        jobUserData.forEach(row => {
            if ((row.Forecast_Tab || row.tab) !== 'Change Order Signature') return;
            if (row.BU_ID != selectedJob) return;
            if ((row.Version_GUID || row.version) !== String(selectedVersion)) return;
            const coNo = row.Row_Label || row.rowLabel || '';
            if (!coNo) return;
            const raw = row.Notes || row.notes || '';
            try { map[coNo] = raw ? JSON.parse(raw) : { status: row.Forecast_Value || row.value || '' }; }
            catch { map[coNo] = { status: row.Forecast_Value || row.value || '' }; }
        });
        return map;
    }, [jobUserData, selectedJob, selectedVersion]);

    // Compact read-only signature badge for the CO cell. Internal-change rows are
    // exempt; full detail lives in the hover title.
    const renderSigBadge = (r) => {
        const coNo = r.Row_Label || '';
        const key = coDocKey(coNo);
        if (!coNo || (key && key[0] === 'I')) return null;
        if (/internal/i.test(r.Type || '')) return null;
        const res = sigResults[coNo];
        if (!res) return null;
        let color = '#6b7280', icon = 'MdHelpOutline', label = '';
        const title = `${res.status}` +
            (res.source ? ` · via ${res.source}` : '') +
            (res.docDate ? ` · doc date ${res.docDate} (${res.dateSource || '?'})` : '') +
            (res.emailApprover ? ` · ${res.emailApprover}` : '') +
            (res.fileName ? ` · ${res.fileName}` : '');
        if (res.status === 'signed') {
            color = '#16a34a'; icon = 'MdCheckCircle';
            label = res.source === 'email' ? 'email' : (res.dateMatches === true ? 'date OK' : '');
        } else if (res.status === 'date_mismatch') {
            color = '#f59e0b'; icon = 'MdWarning'; label = 'date differs';
        } else if (res.status === 'unsigned') {
            color = '#dc2626'; icon = 'MdError'; label = 'not signed';
        } else if (res.status === 'no_doc') {
            color = '#f59e0b'; icon = 'MdWarning'; label = 'no signed doc';
        } else {
            return null;
        }
        return (
            <View style={{ flexDirection: 'row', alignItems: 'center', marginLeft: 4 }} title={title}>
                <Icon3 name={icon} size={13} color={color} />
                {label ? <Text style={{ marginLeft: 2, fontSize: 9, color }}>{label}</Text> : null}
            </View>
        );
    };

    const COLORS = {
        navy: "#173659",
        navyDark: "#0F2A4A",
        headerText: "#FFFFFF",
        subHeader: "#224774",
        grid: "#B7C4D3",
        zebra: "#F6F8FB",
        rowHighlight: "#E5F3EA",
        cellText: "#0E1B2B",
        white: "#FFFFFF",
    };

    const COLS = {
        coNo: 65,
        type: 140,
        revenueType: 135,
        description: 200,
        status: 144,
        date: 108,
        costCredit: 80,
        cumulativeAmount: 90,
        timeExt: 80,
        timeCum: 70,
        notes: 300,
    };

    const bw = StyleSheet.hairlineWidth;

    const totalWidth =
        COLS.coNo +
        COLS.type +
        COLS.revenueType +
        COLS.description +
        COLS.status +
        COLS.date * 3 +
        COLS.costCredit +
        COLS.cumulativeAmount +
        COLS.timeExt +
        COLS.timeCum +
        90 +
        COLS.notes;

    const fmt = (v) =>
        v === 0 || (v && v.toString)
            ? v.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            : "";

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return "";
            // Add 6 hours to handle timezone offset issues
            date.setHours(date.getHours() + 6);
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const month = monthNames[date.getMonth()];
            const day = date.getDate();
            const year = date.getFullYear();
            return `${month} ${day}, ${year}`;
        } catch {
            return "";
        }
    };

    // Load and process data
    useEffect(() => {
        if (jobUserData && selectedJob && selectedVersion) {
            loadChangeOrderData();
        }
    }, [jobUserData, selectedJob, selectedVersion]);

    // Set OH percent from overheadData
    useEffect(() => {
        if (selectedTeam && overheadData) {
            setOhPercent(overheadData[selectedTeam] ? overheadData[selectedTeam] : 0);
        }
    }, [selectedTeam, overheadData]);

    // Set refs for PSR slide export
    useEffect(() => {
        if (a.photo && a.setJobRef && customerViewSections.length > 0) {
            // Create refs for all sections
            customerViewSections.forEach((_, index) => {
                if (!sectionRefs.current[index]) {
                    sectionRefs.current[index] = React.createRef();
                }
            });

            // Include all sections (including internal-change) for PSR slide
            const allRefs = customerViewSections.map((_, index) => sectionRefs.current[index]);

            // Pass all refs to PSR slide job
            a.setJobRef(a.job, 'coLog', allRefs);
        }
    }, [customerViewSections, a.photo, a.job]);

    const loadChangeOrderData = () => {
        try {
            const processedCoRows = processCoRowsData();
            const processedCustomerView = processCustomerViewData(processedCoRows);
            setCustomerViewRows(processedCustomerView.rows);
            setCustomerViewSections(processedCustomerView.sections);
        } catch (error) {
            console.error('Error loading change order data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const processCoRowsData = () => {
        if (!jobUserData) return [];

        const coData = jobUserData.filter(row =>
            row.Forecast_Tab === "Change Order" &&
            row.BU_ID == selectedJob &&
            row.Version_GUID == selectedVersion.toString()
        );

        return coData.map(row => ({
            ...row,
            Row_Label: row.rowLabel || row.Row_Label || '',
            Cost_Code: row.costCode || row.Cost_Code || '',
            Cost_Type: row.costType || row.Cost_Type || '',
            Value_Amount: row.valueAmount || row.Value_Amount || 0,
            Hours: row.hours || row.Hours || 0,
            Quantity: row.quantity || row.Quantity || 0,
            Resource_Code: row.resourceCode || row.Resource_Code || '',
            Status: row.status || row.Status || ''
        }));
    };

    const processCustomerViewData = (cologs) => {
        if (!jobUserData) return { rows: [], sections: [] };

        // First, calculate revenue and costs from cologs for each CO
        const coRevenueMap = {};
        const coCostMap = {};
        const coRevenueObject = {};
        
        if (cologs && cologs.length > 0) {
            cologs.forEach(row => {
                const coId = row.Row_Label || '';
                const dollars = pNum(row.Value_Amount || 0);

                if (!coId) return;

                // Initialize all maps for this CO if not exists
                if (!coRevenueMap[coId]) {
                    coRevenueMap[coId] = 0;
                    coRevenueObject[coId] = [];
                }

                if (!coCostMap[coId]) {
                    coCostMap[coId] = 0;
                }

                // Revenue: Cost_Code is 41000
                if (row.Cost_Type && row.Cost_Type[0] == '4') {
                    coRevenueMap[coId] += dollars;
                }
                
                // Costs: Cost_Type starts with '5' (approved costs)
                if (row.Cost_Type && row.Cost_Type[0] == '5') {
                    coCostMap[coId] += dollars;
                }
                
                coRevenueObject[coId].push(row);
            });
        }
        console.log('CO Revenue Map:', cologs, coRevenueMap, coCostMap, coRevenueObject);

        // Get CO Log header data
        const coLogData = jobUserData.filter(row =>
            row.Forecast_Tab === "Change Order Header" &&
            row.BU_ID == selectedJob &&
            row.Version_GUID == selectedVersion.toString()
        );

        // Also get cost rows to find COs that may not have headers
        const coCostRows = jobUserData.filter(row =>
            row.Forecast_Tab === "Change Order" &&
            row.BU_ID == selectedJob &&
            row.Version_GUID == selectedVersion.toString()
        );

        // Group by CO ID and aggregate data
        const coGroups = {};

        // First pass: build from headers
        coLogData.forEach(row => {
            const coId = row.rowLabel || row.Row_Label || '';
            if (!coId) return;

            if (!coGroups[coId]) {
                const revenue = coRevenueMap[coId] || 0;
                const cost = coCostMap[coId] || 0;
                const margin = revenue - cost; // job profit
                
                console.log('overhead percent:', ohPercent);
                // Calculate GM%: (job profit - overhead) / revenue
                let gmPercent = 0;
                if (revenue !== 0) {
                    const overhead = (pNum(ohPercent) / 100) * revenue; // overhead dollars
                    const gm = margin - overhead; // gm dollars
                    gmPercent = gm / revenue; // gm%
                }

                const mapType = {
                    "Internal C": "Internal Change",
                    "Customer R": "Customer Requested",
                    "Unit Adder": "Unit Adder",
                    "Bid Miss": "Bid Miss"
                };
                
                coGroups[coId] = {
                    Row_Label: coId,
                    Status: row.status || row.Status || '',
                    Type: mapType[row.PO_Number] || row.PO_Number || '',
                    Description: row.description || row.Description || '',
                    Revenue: revenue,
                    Extension: row.valueAmount || row.Value_Amount || 0,
                    Task: row.active || row.Active || false,
                    'Critical Path': row.equipmentTier || false,
                    'GM%': gmPercent,
                    Notes: row.notes || row.Notes || '',
                    'Revenue Type': row.resourceCode || row.Resource_Code || '',
                    Initiated: row.projectStart || row.Project_Start || '',
                    Submitted: row.projectEnd || row.Project_End || '',
                    Signed: row.phaseStart || row.Phase_Start || ''
                };
            }
        });

        // Second pass: create entries for COs that only have cost rows but no header
        coCostRows.forEach(row => {
            const coId = row.rowLabel || row.Row_Label || '';
            if (!coId || coGroups[coId]) return;

            const revenue = coRevenueMap[coId] || 0;
            const cost = coCostMap[coId] || 0;
            const margin = revenue - cost;
            let gmPercent = 0;
            if (revenue !== 0) {
                const overhead = (pNum(ohPercent) / 100) * revenue;
                const gm = margin - overhead;
                gmPercent = gm / revenue;
            }

            coGroups[coId] = {
                Row_Label: coId,
                Status: row.status || row.Status || '',
                Type: '',
                Description: '',
                Revenue: revenue,
                Extension: 0,
                Task: false,
                'Critical Path': false,
                'GM%': gmPercent,
                Notes: '',
                'Revenue Type': '',
                Initiated: '',
                Submitted: '',
                Signed: ''
            };
        });

        console.log('CO Groups after aggregation:', coGroups, coLogData, cologs);
        const rows = Object.values(coGroups);

        // Sort by CO ID numerically
        rows.sort((a, b) => {
            const aNum = parseFloat(a.Row_Label) || 0;
            const bNum = parseFloat(b.Row_Label) || 0;
            return aNum - bNum;
        });

        // Divide into sections first
        const sections = divideCustomerViewIntoSections(rows);

        // Calculate cumulative amounts per section
        sections.forEach(section => {
            let cumulativeCost = 0;
            section.rows.forEach(row => {
                cumulativeCost += pNum(row.Revenue || 0);
                row['Cumulative Amount to Date'] = cumulativeCost;
            });
        });

        return { rows, sections };
    };

    const processCoLogsData = () => {
        if (!jobUserData) return [];

        // This would process legacy co logs data if it exists
        const coLogLegacyData = jobUserData.filter(row =>
            row.tab === "CO Log" &&
            row.fieldName &&
            row.id === selectedJob &&
            row.version === selectedVersion.toString()
        );

        return coLogLegacyData;
    };

    const divideCustomerViewIntoSections = (rows) => {
        // Separate rows by status and type (check both Type and PO_Number fields)
        const internalChangeRows = rows.filter(row =>
            row.Type === 'Internal Change' ||
            row.PO_Number === 'Internal Change' ||
            (row.Type && row.Type.toLowerCase().includes('internal'))
        );
        const approvedRows = rows.filter(row =>
            (row.Status === 'Approved' || row.Status === 'approved') &&
            row.Type !== 'Internal Change' &&
            row.PO_Number !== 'Internal Change' &&
            !(row.Type && row.Type.toLowerCase().includes('internal'))
        );
        const otherRows = rows.filter(row =>
            (row.Status !== 'Approved' && row.Status !== 'approved') &&
            row.Type !== 'Internal Change' &&
            row.PO_Number !== 'Internal Change' &&
            !(row.Type && row.Type.toLowerCase().includes('internal'))
        );

        const sections = [];

        if (approvedRows.length > 0) {
            sections.push({ type: 'approved', rows: approvedRows });
        }

        if (otherRows.length > 0) {
            sections.push({ type: 'other', rows: otherRows });
        }

        if (internalChangeRows.length > 0) {
            sections.push({ type: 'internal-change', rows: internalChangeRows });
        }

        return sections;
    };

    const CustomerHeader = () => (
        <View style={{
            flexDirection: "row",
            borderBottomWidth: bw,
            borderColor: COLORS.grid,
            backgroundColor: COLORS.navyDark,
            height: 30,
            marginTop: 4
        }}>
            {[
                { label: "CO No.", width: COLS.coNo },
                { label: "Type", width: COLS.type },
                { label: "Rev Type", width: COLS.revenueType },
                { label: "Description", width: COLS.description },
                { label: "Status", width: COLS.status },
                { label: "Initiated", width: COLS.date },
                { label: "Submitted", width: COLS.date },
                { label: "Signed", width: COLS.date },
                { label: "Revenue", width: COLS.costCredit },
                { label: "Cumulative", width: COLS.cumulativeAmount },
                { label: "Extension", width: COLS.timeExt },
                { label: "Task", width: COLS.timeCum },
                { label: "Critical", width: COLS.timeCum },
                { label: "GM%", width: COLS.timeCum },
                { label: "Notes", width: COLS.notes },
            ].map((h, i) => (
                <View
                    key={`header-${i}`}
                    style={{
                        width: h.width,
                        borderRightWidth: bw,
                        borderColor: COLORS.grid,
                        justifyContent: "center",
                        backgroundColor: COLORS.navy,
                        padding: '0 6px',
                        borderRight: '1px solid #aeadaf'
                    }}
                >
                    <Text style={{
                        color: COLORS.headerText,
                        fontWeight: "600",
                        fontSize: 11,
                        lineHeight: 13,
                        paddingVertical: 4,
                        paddingHorizontal: 4,
                    }}>
                        {h.label}
                    </Text>
                </View>
            ))}
        </View>
    );

    const ApprovedCustomerHeader = () => (
        <View style={{
            flexDirection: "row",
            borderBottomWidth: bw,
            borderColor: COLORS.grid,
        }}>
            <View style={{
                width: totalWidth,
                justifyContent: "center",
                paddingVertical: 6,
                paddingHorizontal: 8,
            }}>
                <Text style={{
                    color: COLORS.cellText,
                    fontWeight: "700",
                    fontSize: 14,
                    textAlign: "left"
                }}>
                    Approved Change Orders
                </Text>
            </View>
        </View>
    );

    const NonApprovedCustomerHeader = () => (
        <View style={{
            flexDirection: "row",
            borderBottomWidth: bw,
            borderColor: COLORS.grid,
        }}>
            <View style={{
                width: totalWidth,
                justifyContent: "center",
                paddingVertical: 6,
                paddingHorizontal: 8,
            }}>
                <Text style={{
                    color: COLORS.cellText,
                    fontWeight: "700",
                    fontSize: 14,
                    textAlign: "left"
                }}>
                    Non-Approved Change Orders
                </Text>
            </View>
        </View>
    );

    const InternalChangeCustomerHeader = () => (
        <View style={{
            flexDirection: "row",
            borderBottomWidth: bw,
            borderColor: COLORS.grid,
        }}>
            <View style={{
                width: totalWidth,
                justifyContent: "center",
                paddingVertical: 6,
                paddingHorizontal: 8,
            }}>
                <Text style={{
                    color: COLORS.cellText,
                    fontWeight: "700",
                    fontSize: 14,
                    textAlign: "left"
                }}>
                    Internal Change Orders
                </Text>
            </View>
        </View>
    );

    const CustomerFooter = () => (
        <View style={{
            flexDirection: "row",
            borderTopWidth: bw,
            borderColor: COLORS.grid,
            backgroundColor: COLORS.subHeader,
        }}>
            <View style={{
                width: totalWidth,
                justifyContent: "center",
                paddingVertical: 6,
                paddingHorizontal: 8,
            }}>
                <Text style={{
                    color: COLORS.headerText,
                    fontWeight: "700",
                    fontSize: 13,
                    textAlign: "center"
                }}>
                </Text>
            </View>
        </View>
    );

    // The client-facing workbook: Approved / Non-Approved blocks with cumulative
    // revenue, the same file the Change Orders folder on the client site carries.
    const exportToExcel = async () => {
        if (exporting || !customerViewSections?.length) return;
        setExporting(true);
        try {
            await downloadCoLog(customerViewSections, { job: selectedJob });
        } catch (e) {
            console.error('CO Log export failed', e);
        } finally {
            setExporting(false);
        }
    };

    return (
        <View style={{ flex: 1, backgroundColor: '#fff' }}>
            <ScrollView>
                <View style={{ backgroundColor: 'white', borderRadius: 5, padding: 20, margin: 30, marginBottom: 100, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 3 }}>
                    {customerViewSections && customerViewSections.length > 0 ? (
                        <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginBottom: 10 }}>
                            <Pressable
                                onPress={exportToExcel}
                                disabled={exporting}
                                title="Download the Approved / Non-Approved change order log as an Excel workbook"
                                style={{
                                    flexDirection: 'row', alignItems: 'center', paddingVertical: 6, paddingLeft: 10, paddingRight: 12,
                                    borderRadius: 4, borderWidth: 1, borderColor: COLORS.navy, backgroundColor: exporting ? '#F1F5F9' : COLORS.white,
                                    opacity: exporting ? 0.7 : 1,
                                }}
                            >
                                <Icon3 name="MdDownload" size={15} color={COLORS.navy} />
                                <Text style={{ marginLeft: 6, fontSize: 12, fontWeight: '600', color: COLORS.navy }}>
                                    {exporting ? 'Exporting…' : 'Export to Excel'}
                                </Text>
                            </Pressable>
                        </View>
                    ) : null}
                    {/* Customer View Sections */}
                    {customerViewSections && customerViewSections.length > 0 ? (
                        customerViewSections.map((section, sectionIndex) => {
                            // Create ref for this section if it doesn't exist
                            if (!sectionRefs.current[sectionIndex]) {
                                sectionRefs.current[sectionIndex] = React.createRef();
                            }

                            return (
                                <View
                                    key={sectionIndex}
                                    ref={sectionRefs.current[sectionIndex]}
                                    style={{
                                        width: totalWidth,
                                        borderLeftWidth: bw,
                                        borderTopWidth: bw,
                                        borderColor: COLORS.grid,
                                        backgroundColor: COLORS.white,
                                        marginBottom: sectionIndex < customerViewSections.length - 1 ? 30 : 0,
                                    }}
                                >

                                    {/* Use appropriate header based on section type */}
                                    {section.type === 'approved' ? <ApprovedCustomerHeader /> :
                                        section.type === 'other' ? <NonApprovedCustomerHeader /> :
                                            section.type === 'internal-change' ? <InternalChangeCustomerHeader /> :
                                                null}

                                    {/* Column Headers */}
                                    <CustomerHeader />

                                    {/* Data Rows */}
                                    {(section.rows || section).map((r, idx) => {
                                        const bg = r.highlight ? COLORS.rowHighlight : idx % 2 ? COLORS.zebra : COLORS.white;
                                        return (
                                            <View
                                                key={idx}
                                                style={{
                                                    flexDirection: "row",
                                                    borderTopWidth: bw,
                                                    borderColor: COLORS.grid,
                                                    backgroundColor: bg,
                                                }}
                                            >
                                                {/* C.O. No. */}
                                                <View style={{
                                                    width: COLS.coNo,
                                                    borderRightWidth: bw,
                                                    borderColor: COLORS.grid,
                                                    paddingVertical: 4,
                                                    paddingHorizontal: 4,
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    flexDirection: "row",
                                                }}>
                                                    <Text numberOfLines={1} style={{
                                                        color: COLORS.cellText,
                                                        fontSize: 13,
                                                        textAlign: "center",
                                                        fontWeight: "500"
                                                    }}>
                                                        {r.Row_Label ?? ""}
                                                    </Text>
                                                    {/* TEMP: hide missing-SharePoint-doc warning icon
                                                    {isCoMissingDoc(r) && (
                                                        <View style={{ marginLeft: 4 }} title={coDocsTooltip}>
                                                            <Icon3 name="MdWarning" size={13} color="#f59e0b" />
                                                        </View>
                                                    )}
                                                    */}
                                                    {renderSigBadge(r)}
                                                </View>

                                                {/* Type */}
                                                <View style={{
                                                    width: COLS.type,
                                                    borderRightWidth: bw,
                                                    borderColor: COLORS.grid,
                                                    paddingVertical: 4,
                                                    paddingHorizontal: 4,
                                                    justifyContent: "center",
                                                }}>
                                                    <Text numberOfLines={1} style={{ color: COLORS.cellText, fontSize: 12 }}>
                                                        {r.Type ?? ""}
                                                    </Text>
                                                </View>

                                                {/* Revenue Type */}
                                                <View style={{
                                                    width: COLS.revenueType,
                                                    borderRightWidth: bw,
                                                    borderColor: COLORS.grid,
                                                    paddingVertical: 4,
                                                    paddingHorizontal: 4,
                                                    justifyContent: "center",
                                                }}>
                                                    <Text numberOfLines={1} style={{ color: COLORS.cellText, fontSize: 12 }}>
                                                        {r['Revenue Type'] ?? ""}
                                                    </Text>
                                                </View>

                                                {/* Description */}
                                                <View style={{
                                                    width: COLS.description,
                                                    borderRightWidth: bw,
                                                    borderColor: COLORS.grid,
                                                    paddingVertical: 4,
                                                    paddingHorizontal: 4,
                                                    justifyContent: "center",
                                                    overflow: "hidden",
                                                }}>
                                                    <Text numberOfLines={1} style={{ color: COLORS.cellText, fontSize: 12 }}>
                                                        {r.Description ?? ""}
                                                    </Text>
                                                </View>

                                                {/* Status */}
                                                <View style={{
                                                    width: COLS.status,
                                                    borderRightWidth: bw,
                                                    borderColor: COLORS.grid,
                                                    paddingVertical: 4,
                                                    paddingHorizontal: 4,
                                                    justifyContent: "center",
                                                }}>
                                                    <Text numberOfLines={1} style={{ color: COLORS.cellText, fontSize: 12, fontWeight: "500" }}>
                                                        {r.Status ?? ""}
                                                    </Text>
                                                </View>

                                                {/* Dates: Initiated / Submitted / Signed */}
                                                {[
                                                    r?.Initiated ?? "",
                                                    r?.Submitted ?? "",
                                                    r?.Signed ?? "",
                                                ].map((val, i) => (
                                                    <View
                                                        key={`d-${i}`}
                                                        style={{
                                                            width: COLS.date,
                                                            borderRightWidth: bw,
                                                            borderColor: COLORS.grid,
                                                            paddingVertical: 4,
                                                            paddingHorizontal: 4,
                                                            justifyContent: "center",
                                                        }}
                                                    >
                                                        <Text numberOfLines={1} style={{
                                                            color: COLORS.cellText,
                                                            fontSize: 12,
                                                            textAlign: "center"
                                                        }}>
                                                            {formatDate(val)}
                                                        </Text>
                                                    </View>
                                                ))}

                                                {/* Cost/(Credit) */}
                                                <View style={{
                                                    width: COLS.costCredit,
                                                    borderRightWidth: bw,
                                                    borderColor: COLORS.grid,
                                                    paddingVertical: 4,
                                                    paddingHorizontal: 4,
                                                    justifyContent: "center",
                                                }}>
                                                    <Text numberOfLines={1} style={{
                                                        color: COLORS.cellText,
                                                        fontSize: 12,
                                                        textAlign: "right",
                                                        fontWeight: "500"
                                                    }}>
                                                        {(fNum(r['Revenue'], 1) ?? 0)}
                                                    </Text>
                                                </View>

                                                {/* Cumulative Amount to Date */}
                                                <View style={{
                                                    width: COLS.cumulativeAmount,
                                                    borderRightWidth: bw,
                                                    borderColor: COLORS.grid,
                                                    paddingVertical: 4,
                                                    paddingHorizontal: 4,
                                                    justifyContent: "center",
                                                }}>
                                                    <Text numberOfLines={1} style={{
                                                        color: COLORS.cellText,
                                                        fontSize: 12,
                                                        textAlign: "right"
                                                    }}>
                                                        {(fNum(r['Cumulative Amount to Date'], 1) ?? 0)}
                                                    </Text>
                                                </View>

                                                {/* Time: Extension */}
                                                <View style={{
                                                    width: COLS.timeExt,
                                                    borderRightWidth: bw,
                                                    borderColor: COLORS.grid,
                                                    paddingVertical: 4,
                                                    paddingHorizontal: 4,
                                                    justifyContent: "center",
                                                }}>
                                                    <Text numberOfLines={1} style={{
                                                        color: COLORS.cellText,
                                                        fontSize: 12,
                                                        textAlign: "right"
                                                    }}>
                                                        {fmt(r['Extension'] ?? 0)}
                                                    </Text>
                                                </View>

                                                {/* Task Checkbox */}
                                                <View style={{
                                                    width: COLS.timeCum,
                                                    borderRightWidth: bw,
                                                    borderColor: COLORS.grid,
                                                    paddingVertical: 4,
                                                    paddingHorizontal: 2,
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                }}>
                                                    <Text numberOfLines={1} style={{
                                                        color: COLORS.cellText,
                                                        fontSize: 16,
                                                        textAlign: "center"
                                                    }}>
                                                        {r.Task ? '☑' : '☐'}
                                                    </Text>
                                                </View>

                                                {/* Critical Path Checkbox */}
                                                <View style={{
                                                    width: COLS.timeCum,
                                                    borderRightWidth: bw,
                                                    borderColor: COLORS.grid,
                                                    paddingVertical: 4,
                                                    paddingHorizontal: 2,
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                }}>
                                                    <Text numberOfLines={1} style={{
                                                        color: COLORS.cellText,
                                                        fontSize: 16,
                                                        textAlign: "center"
                                                    }}>
                                                        {r['Critical Path'] ? '☑' : '☐'}
                                                    </Text>
                                                </View>

                                                {/* GM% */}
                                                <View style={{
                                                    width: COLS.timeCum,
                                                    borderRightWidth: bw,
                                                    borderColor: COLORS.grid,
                                                    paddingVertical: 4,
                                                    paddingHorizontal: 4,
                                                    justifyContent: "center",
                                                }}>
                                                    <Text numberOfLines={1} style={{
                                                        color: COLORS.cellText,
                                                        fontSize: 12,
                                                        textAlign: "center"
                                                    }}>
                                                        {r['GM%'] != null ? (r['GM%'] * 100).toFixed(0) + "%" : ""}
                                                    </Text>
                                                </View>

                                                {/* Notes */}
                                                <View style={{
                                                    width: COLS.notes,
                                                    borderRightWidth: bw,
                                                    borderColor: COLORS.grid,
                                                    paddingVertical: 4,
                                                    paddingHorizontal: 4,
                                                    justifyContent: "center",
                                                    overflow: "hidden",
                                                }}>
                                                    <Text numberOfLines={1} style={{ color: COLORS.cellText, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {r.Notes ? r.Notes : ""}
                                                    </Text>
                                                </View>
                                            </View>
                                        );
                                    })}
                                    <CustomerFooter />
                                </View>
                        );
                    })
                ) : (
                    <View style={{ padding: 40, alignItems: 'center' }}>
                        <Text style={{ fontSize: 16, color: isLoading ? '#2563eb' : '#666', fontWeight: isLoading ? '600' : '400' }}>
                            {isLoading ? 'Loading change order data...' : 'No CO Log PSR data available. Upload details in PSR tab of excel upload.'}
                        </Text>
                    </View>
                )}
                </View>
            </ScrollView>
        </View>
    );
};

export default COLogPSRView;